import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Download, Eye, Trash2, Receipt } from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import { useToast } from '../../hooks/use-toast';
import { getCsrfToken } from '../../lib/api';
import { Button } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@werkstatt/ui';
import { Badge } from '@werkstatt/ui';
import InvoiceDialog from './InvoiceDialog';
import {
  Invoice,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  formatPrice,
  formatDisplayDate,
  getDaysUntilDue,
} from '../../types/invoice';

export default function Invoices() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [viewMode, setViewMode] = useState(false);

  const pageSize = 50;

  // Fetch invoices
  const { data: invoicesData, isLoading } = useQuery({
    queryKey: ['invoices', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        ...(search && { search }),
      });
      const response = await fetch(`/api/invoices?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch invoices');
      return response.json();
    },
  });

  // Delete invoice mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/invoices/${id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete invoice');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast({
        title: 'Rechnung gelöscht',
        description: 'Die Rechnung wurde erfolgreich gelöscht.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: error.message,
      });
    },
  });

  const handleDelete = (invoice: Invoice) => {
    if (
      window.confirm(
        `Möchten Sie die Rechnung "${invoice.invoice_number}" wirklich löschen?`
      )
    ) {
      deleteMutation.mutate(invoice.id);
    }
  };

  const handleView = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setViewMode(true);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setSelectedInvoice(null);
    setViewMode(false);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedInvoice(null);
    setViewMode(false);
  };

  const totalPages = invoicesData?.total_pages || 0;
  const invoices = invoicesData?.invoices || [];

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Receipt className="h-7 w-7" />
              Rechnungen
            </h1>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Neue Rechnung
          </Button>
        </div>

        {/* Search */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display text-lg">Suche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Suche nach Rechnungsnummer..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display">Rechnungen{invoicesData ? ` (${invoicesData.total})` : ''}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Rechnungsnr.
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Datum
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Fällig
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Betrag
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Bezahlt
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Offen
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                      Lade Rechnungen...
                    </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                      Keine Rechnungen gefunden
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice: Invoice) => {
                    const daysUntilDue = getDaysUntilDue(invoice.due_date);
                    return (
                      <tr key={invoice.id} className="hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div className="font-medium">{invoice.invoice_number}</div>
                          {invoice.is_cancelled && (
                            <div className="text-xs text-red-600">Storniert</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {formatDisplayDate(invoice.invoice_date)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">{formatDisplayDate(invoice.due_date)}</div>
                          {!invoice.is_cancelled &&
                            invoice.payment_status !== 'paid' &&
                            daysUntilDue < 0 && (
                              <div className="text-xs text-red-600">
                                {Math.abs(daysUntilDue)} Tage überfällig
                              </div>
                            )}
                          {!invoice.is_cancelled &&
                            invoice.payment_status !== 'paid' &&
                            daysUntilDue >= 0 &&
                            daysUntilDue <= 7 && (
                              <div className="text-xs text-orange-600">
                                {daysUntilDue === 0
                                  ? 'Heute fällig'
                                  : `in ${daysUntilDue} Tagen fällig`}
                              </div>
                            )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {formatPrice(invoice.total_cents)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {formatPrice(invoice.paid_amount_cents)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {formatPrice(invoice.outstanding_amount_cents)}
                        </td>
                        <td className="px-4 py-3">
                          {invoice.is_cancelled ? (
                            <Badge variant="outline" className="rounded-pill bg-muted">
                              Storniert
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className={PAYMENT_STATUS_COLORS[invoice.payment_status]}
                            >
                              {PAYMENT_STATUS_LABELS[invoice.payment_status]}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(invoice)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {invoice.pdf_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(invoice.pdf_url, '_blank')}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                            {!invoice.is_cancelled && invoice.paid_amount_cents === 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(invoice)}
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Seite {page} von {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Dialog */}
      {dialogOpen && (
        <InvoiceDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          invoice={selectedInvoice}
          viewMode={viewMode}
        />
      )}
    </MainLayout>
  );
}
